import DashboardLayout from "lib/components/layouts/dashboard";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { AppContext } from "lib/contexts/app-context";
import { useContext, useMemo, useState, useEffect } from "react";
import SimpleDataTable from "lib/components/common/simple-data-table";
import { ALL_SESSION_BOOKING_DETAILS_COLUMNS } from "lib/constants/table-columns";
import SimpleCard from "lib/components/common/simple-card";
import TabBar from "lib/components/common/tab-bar";
import { Col, Row } from "react-bootstrap";
import FlexBox from "lib/components/common/flex-box";
import usePatients from "lib/hooks/usePatients";
import { toast } from "sonner";
import {
  PiCalendarLight,
  PiClockLight,
  PiMoneyWavyLight,
} from "react-icons/pi";
import { useNavigate, useSearchParams } from "react-router-dom";
import SimpleModal from "lib/components/common/simple-modal";
import { DEFAULT_PATIENT_SESSION_BOOKING_DETAILS } from "lib/constants/default-values";
import PatientSessionBookingSummaryTile from "../../../lib/components/common/patient/patient-session-booking-summary-tile";

const breadcrumbItems: BreadcrumbItem[] = [
  {
    label: "Dashboard",
    path: PAGE_ROUTES.ADMIN.Dashboard,
  },
  {
    label: "Therapy Sessions",
    path: PAGE_ROUTES.ADMIN.TherapySessions,
  },
];

const PATIENT_APPOINTMENT_TABS: GeneralItem[] = [
  {
    id: 1,
    label: "Upcoming",
    icon: <PiCalendarLight />,
  },
  {
    id: 2,
    label: "Unpaid",
    icon: <PiMoneyWavyLight />,
  },
  {
    id: 3,
    label: "History",
    icon: <PiClockLight />,
  },
];

const TABS = {
  upcoming: 1,
  unpaid: 2,
  history: 3,
};

const AdminTherapySessionsPage = () => {

  const navigateTo = useNavigate();

  const { appState } = useContext(AppContext);

  const { getAllSessionBookingsDetails } = usePatients();

  const [searchParams, setSearchParams] = useSearchParams();

  const [sessionBookingsDetails, setSessionBookingsDetails] = useState<
    SessionBookingDetails[]
  >([]);

  const [showSessionBookingDetails, setShowSessionBookingDetails] = useState<boolean>(false);
  const [selectedSessionBookingDetails, setSelectedSessionBookingDetails] =
    useState<PatientSessionBookingDetails>(
      DEFAULT_PATIENT_SESSION_BOOKING_DETAILS
    );

  const selectedTab = searchParams.get("tab") || "upcoming";
  const selectedTabId = TABS[selectedTab] ?? 0;

  const setSelectedTabId = (tabId: number) => {
    searchParams.set(
      "tab",
      Object.keys(TABS).find((key) => TABS[key] === tabId) || "clinics"
    );
    setSearchParams(searchParams);
  };

  const {
    changeSessionBookingStatus,
    loading: patientsLoading,
    error: patientsError,
  } = usePatients();

  useEffect(() => {
    if (appState.user_id) {
        getAllSessionBookingsDetails((bookingsessions) => {
          setSessionBookingsDetails(bookingsessions);
        });
      }
    }, []);

  const pendingSessionsBookings = useMemo(() => {
    return sessionBookingsDetails.filter((bookingSession) => bookingSession.status === "pending");
  }, [sessionBookingsDetails]);
  
  const scheduledSessionsBookings = useMemo(() => {
    return sessionBookingsDetails.filter((bookingSession) => bookingSession.status === "scheduled");
  }, [sessionBookingsDetails]);

  const completedSessionsBookings = useMemo(() => {
    return sessionBookingsDetails.filter((bookingSession) => bookingSession.status === "completed");
  }, [sessionBookingsDetails]);

  // Handler to update session booking status using API
  const onChangeSessionBookingStatus = async (sessionId: string, newStatus: string) => {
    try {
      await changeSessionBookingStatus(appState.user_id, sessionId, newStatus);
      setSessionBookingsDetails((prev) =>
        prev.map((session) =>
          session.id === sessionId ? { ...session, status: newStatus } : session
        )
      );
      toast.success(`Session status updated to '${newStatus}'.`);
    } catch (error) {
      toast.error('Failed to update session status. Please try again.');
    }
  };

  const onEditSessionBooking = async (sessionId: string) => {
    try {
      navigateTo(PAGE_ROUTES.PATIENT.UpdateTherapySession, {
        state: { sessionId }
      });
    } catch (error) {
      toast.error('Failed to update session details. Please try again.');
    }
  };

  return (
    <DashboardLayout
      breadcrumbItems={breadcrumbItems}
      //isUnavailable={practitionerSubscriptionState.status === "success"}
    >
     <Row>
        <Col sm={12} className="mb-4">
          <SimpleCard>
            <FlexBox>
              <h4>
                {selectedTabId === 1 ? "Therapy Sessions" : "Therapy Sessions History"}
              </h4>
              <TabBar
                tabs={PATIENT_APPOINTMENT_TABS}
                selectedTabId={selectedTabId}
                onTabChange={setSelectedTabId}
              />
            </FlexBox>
            {selectedTabId === 1 && (
              <SimpleDataTable
                data={scheduledSessionsBookings}
                columns={ALL_SESSION_BOOKING_DETAILS_COLUMNS}
                isLoading={patientsLoading}
                defaultSortFieldId="date"
                defaultSortAsc={true}
                onRowClick={(row: PatientSessionBookingDetails) => {
                  setSelectedSessionBookingDetails(row);
                  setShowSessionBookingDetails(true);
                }}
              />
            )}

            {selectedTabId === 2 && (
              <SimpleDataTable
                data={pendingSessionsBookings}
                columns={ALL_SESSION_BOOKING_DETAILS_COLUMNS}
                isLoading={patientsLoading}
                defaultSortFieldId="date"
                defaultSortAsc={true}
                onRowClick={(row: PatientSessionBookingDetails) => {
                  setSelectedSessionBookingDetails(row);
                  setShowSessionBookingDetails(true);
                }}
              />
            )}

            {selectedTabId === 3 && (
              <SimpleDataTable
                data={completedSessionsBookings}
                columns={ALL_SESSION_BOOKING_DETAILS_COLUMNS}
                isLoading={patientsLoading}
                defaultSortFieldId="date"
                defaultSortAsc={true}
                onRowClick={(row: PatientSessionBookingDetails) => {
                  setSelectedSessionBookingDetails(row);
                  setShowSessionBookingDetails(true);
                }}
              />
            )}
          </SimpleCard>
        </Col>
      </Row>
      <SimpleModal
        open={showSessionBookingDetails}
        close={() => setShowSessionBookingDetails(false)}
        title="Session Details"
        size="sm"
      >
        <PatientSessionBookingSummaryTile
          fromTime={selectedSessionBookingDetails.fromTime}
          {...selectedSessionBookingDetails}
          onChangeSessionBookingStatus={onChangeSessionBookingStatus}
          onEditSessionBooking={onEditSessionBooking}
        />
      </SimpleModal>
    </DashboardLayout>
  );
};

export default AdminTherapySessionsPage;
